'use es6';

import ReactDOMServer from 'react-dom/server';
import { supportedScriptPatterns } from 'rich-text-lib/constants/embed';
import { replaceNodes, addPostProcessTransform, addPreProcessTransform } from '../utils/transformer';
import { getEmbedlyOptions } from './util';
import { HS_EMBED_WRAPPER_CLASS, HS_EMBED_CONTENT_WRAPPER_CLASS } from '../constants/hsMedia';
import StaticIframeEmbed from '../components/markup/embeds/StaticIframeEmbed';

// Create a new document that lives in an iframe for security purposes
import { jsx as _jsx } from "react/jsx-runtime";
export const secureDocument = document.implementation.createHTMLDocument('sanitize');
const getWidthHeightFromStyle = styleString => {
  const result = {};
  const heightMatch = /.*?height:\s*(\d+)(?:px).*?/.exec(styleString);
  const widthMatch = /.*?width:\s*(\d+)(?:px).*?/.exec(styleString);
  if (heightMatch) result.height = heightMatch[1];
  if (widthMatch) result.width = widthMatch[1];
  return result;
};
export const getReplacePlaceholderWithHubL = editor => node => {
  const embedType = node.getAttribute('data-embed-type');
  const embedSrc = node.getAttribute('data-embed-src');
  const embedTitle = node.getAttribute('data-embed-title') !== null ? node.getAttribute('data-embed-title') : '';
  let embedOptions = node.getAttribute('data-embed-options');
  const embedDescription = node.getAttribute('data-embed-description');
  if (embedType === 'wistia') {
    const embedStyle = editor.dom.parseStyle(node.getAttribute('style') || '');
    const options = (embedOptions || '').split(',').reduce((acc, option) => {
      if (!/width|height|align|margin/.test(option)) acc.push(option);
      return acc;
    }, []);
    if (embedStyle.height) {
      options.push(`height=${embedStyle.height}`);
    }
    if (embedStyle.width) {
      options.push(`width=${embedStyle.width}`);
    }
    if (embedStyle.float) {
      options.push(`align=${embedStyle.float}`);
    } else if (node.style.marginLeft === 'auto') {
      options.push(`align=center`);
    }
    if (node.style.marginTop) options.push(`marginTop=${node.style.marginTop}`);
    if (node.style.marginRight) options.push(`marginRight=${node.style.marginRight}`);
    if (node.style.marginBottom) options.push(`marginBottom=${node.style.marginBottom}`);
    if (node.style.marginLeft) options.push(`marginLeft=${node.style.marginLeft}`);
    embedOptions = options.join(',');
  }
  let scriptEmbedString = `{{ script_embed('${embedType}', '${embedSrc}', '${embedTitle}'`;
  if (embedOptions) scriptEmbedString += `, '${embedOptions}'`;
  if (embedDescription) scriptEmbedString += `, '${embedDescription}'`;
  scriptEmbedString += `) }}`;
  return document.createTextNode(scriptEmbedString);
};
const applyPreprocessToEmbedContent = (editor, domContent) => {
  let content = domContent;
  Object.keys(supportedScriptPatterns).forEach(patternName => {
    const pattern = supportedScriptPatterns[patternName];
    let matches = pattern.exec(content);
    while (matches !== null) {
      if (matches.length) {
        let patternType;
        let src;
        let title;
        let embedOptions;
        let embedStyles;
        let embedDescription;
        let height;
        let width;
        let styleObj;
        if (patternName === 'scriptEmbed') {
          const matchedArgs = matches[1].split(/['"],\W['"]/).map(arg => arg.replace(/['"]/g, ''));
          matchedArgs[matchedArgs.length - 1] = matchedArgs[matchedArgs.length - 1].replace(/['"],\W*$/, '');
          matchedArgs.push(matches[matches.length - 1]);
          patternType = matchedArgs[0];
          src = matchedArgs[1];
          title = matchedArgs[2];
          if (matchedArgs.length >= 4) {
            embedOptions = [matchedArgs[3]];
            embedStyles = {};
            embedOptions[0].split(',').forEach(option => {
              const [opt, val] = option.split('=');
              if (opt === 'height') {
                embedStyles.height = val;
              } else if (opt === 'width') {
                embedStyles.width = val;
              } else if (opt === 'align') {
                if (val === 'right' || val === 'left') {
                  embedStyles.float = val;
                } else if (val === 'center') {
                  embedStyles['margin-right'] = 'auto';
                  embedStyles['margin-left'] = 'auto';
                  embedStyles.display = 'block';
                }
              } else if (opt === 'marginTop') {
                embedStyles['margin-top'] = val;
              } else if (opt === 'marginBottom') {
                embedStyles['margin-bottom'] = val;
              } else if (opt === 'marginLeft' && embedStyles['margin-left'] !== 'auto') {
                embedStyles['margin-left'] = val;
              } else if (opt === 'marginRight' && embedStyles['margin-right'] !== 'auto') {
                embedStyles['margin-right'] = val;
              }
            });
            embedStyles = editor.dom.serializeStyle(embedStyles);
          }
          if (matchedArgs.length >= 5) {
            embedDescription = matchedArgs[4];
          }
        } else if (patternName === 'wistiaInlineResponsive') {
          patternType = 'wistia';
          src = matches[1];
          embedOptions = ['inline', 'responsive'];
        } else if (patternName === 'wistiaInlineFixed') {
          patternType = 'wistia';
          src = matches[1];
          styleObj = getWidthHeightFromStyle(matches[2]);
          height = styleObj.height;
          width = styleObj.width;
          embedOptions = ['inline'];
          embedStyles = '';
          if (height) {
            embedOptions.push(`height=${height}px`);
            embedStyles += `height:${height}px;`;
          }
          if (width) {
            embedOptions.push(`width=${width}px`);
            embedStyles += `width:${width}px;`;
          }
        } else if (patternName === 'wistiaPopoverThumbnail') {
          patternType = 'wistia';
          src = matches[1];
          styleObj = getWidthHeightFromStyle(matches[2]);
          height = styleObj.height;
          width = styleObj.width;
          embedOptions = ['popover'];
          if (matches[0].indexOf('<div class="wistia_responsive_wrapper"') >= 0) {
            embedOptions.push(['responsive']);
          } else {
            embedStyles = '';
            if (height) {
              embedOptions.push(`height=${height}px`);
              embedStyles += `height:${height}px;`;
            }
            if (width) {
              embedOptions.push(`width=${width}px`);
              embedStyles += `width:${width}px;`;
            }
          }
        } else if (patternName === 'wistiaPopoverTextLink') {
          patternType = 'wistia';
          src = matches[1];
          title = matches[2];
          embedOptions = ['popover', 'link'];
        } else if (patternName === 'embedlyPlain') {
          patternType = 'embedly';
          src = matches[1];
          title = matches[2];
          embedOptions = getEmbedlyOptions(content);
        } else if (patternName === 'embedlySeo') {
          patternType = 'embedly';
          src = matches[1];
          title = matches[2];
          embedDescription = matches[3].replace(/\\/g, '\\\\').replace(/'/g, '\\&#39;').replace(/"/g, '\\&quot;');
          embedOptions = getEmbedlyOptions(content);
        }
        let replacement = `<img class='mce-object hs-embeddable' width=${width || 425} height=${height || 350} src='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' data-embed-type='${patternType}' data-embed-src='${src}' data-mce-placeholder `;
        if (title) replacement += `data-embed-title='${title}' `;
        if (embedOptions) replacement += `data-embed-options="${embedOptions}" `;
        if (embedDescription) replacement += `data-embed-description="${embedDescription}" `;
        if (embedStyles) replacement += `style="${embedStyles}"`;
        replacement += "data-mce-resize='false' />";
        content = content.replace(pattern, replacement);
      }
      // iterate through for the while loop (since due to style we can't do a while with assign)
      matches = pattern.exec(content);
    }
  });
  return content;
};
export const addTransforms = editor => {
  addPreProcessTransform(editor, dom => {
    dom.body.innerHTML = applyPreprocessToEmbedContent(editor, dom.body.innerHTML);
  });
  addPostProcessTransform(editor, dom => {
    const embedPlaceholderNodes = dom.querySelectorAll('.mce-object.hs-embeddable');
    replaceNodes(embedPlaceholderNodes, getReplacePlaceholderWithHubL(editor));
  });
};

/**
 * NEW IFRAME EMBED TRANSFORMATIONS
 */

let iframeEmbedId = 0;
const getIframeEmbedId = editorId => `${editorId}-mce-embed-iframe-${++iframeEmbedId}`;

// Remove the innerHTML of each [HS_EMBED_WRAPPER_CLASS] div and save
// the embed content as a data attribute (`data-mce-iframe-embed-content`)
const getCreateIframeEmbedPlaceholder = editor => node => {
  const placeholder = secureDocument.importNode(node, false);
  placeholder.classList.add('mce-preview-object', 'mceNonEditable');
  placeholder.contentEditable = false;

  // Always save the embed content as a data attribute on the wrapper
  const embedContentWrapper = node.querySelector(`.${HS_EMBED_CONTENT_WRAPPER_CLASS}`);
  placeholder.dataset.mceEmbedContent = embedContentWrapper.innerHTML;
  const hasScripts = !!placeholder.dataset.scriptEmbed;
  if (hasScripts) {
    // Use the [content-embed-ui] app structure for script embeds.
    // Set the innerHTML of the wrapper with an inner wrapper & embed iframe
    const embedId = getIframeEmbedId(editor.id);
    placeholder.dataset.mceIframeEmbedId = embedId;
    placeholder.innerHTML = ReactDOMServer.renderToStaticMarkup( /*#__PURE__*/_jsx(StaticIframeEmbed, {
      inpage: !!editor.settings.hsInpage,
      embedId: embedId
    }));
  } else {
    // Otherwise add a shim sibling node for selection purposes
    placeholder.innerHTML = embedContentWrapper.outerHTML;
    const shimNode = secureDocument.createElement('span');
    shimNode.className = 'mce-shim';
    placeholder.appendChild(shimNode);
  }
  return placeholder;
};

// Add back the innerHTML of each [HS_EMBED_WRAPPER_CLASS] div
// and remove all `data-mce-*` attributes
const convertBackFromIframeEmbedPlaceholder = node => {
  // Clone the node so we don't impact the real DOM node
  const realElm = secureDocument.importNode(node, true);
  realElm.style.removeProperty('max-height');

  // Remove mce attributes from the wrapper
  realElm.classList.remove('mce-preview-object', 'mceNonEditable');
  [...realElm.attributes].forEach(({
    name
  }) => {
    if (name.indexOf('data-mce-') !== -1) {
      realElm.removeAttribute(name);
    }
  });

  // Create a clean embed content wrapper
  const embedContentWrapper = secureDocument.createElement('div');
  embedContentWrapper.className = HS_EMBED_CONTENT_WRAPPER_CLASS;
  embedContentWrapper.innerHTML = node.dataset.mceEmbedContent;
  realElm.innerHTML = embedContentWrapper.outerHTML;
  return realElm;
};
const applyNewPostProcessTransforms = (__editor, body) => {
  const newBody = body.cloneNode(true);
  const wrappers = newBody.querySelectorAll(`.${HS_EMBED_WRAPPER_CLASS}`);
  Array.from(wrappers).forEach(wrapper => {
    const realElm = convertBackFromIframeEmbedPlaceholder(wrapper);
    wrapper.parentElement.replaceChild(realElm, wrapper);
  });
  return newBody.innerHTML;
};
export const addNewTransforms = editor => {
  addPreProcessTransform(editor, dom => {
    const iframeWrapperNodes = dom.body.querySelectorAll(`.${HS_EMBED_WRAPPER_CLASS}`);
    replaceNodes(iframeWrapperNodes, getCreateIframeEmbedPlaceholder(editor));
  });
  addPostProcessTransform(editor, dom => {
    dom.body.innerHTML = applyNewPostProcessTransforms(editor, dom.body);
  });
};